/**
 * Helper functions for displaying aggregator status information
 * These utilities provide consistent status display logic for aggregator views
 */

import { AggregatorStatusResponse } from 'src/interfaces/aggregator.interfaces';
import { AGGREGATOR_STATUS } from './aggregator.constants';

/**
 * Gets a user-friendly status display string from aggregator status data
 */
export const getAggregatorStatusDisplay = (statusData: AggregatorStatusResponse | null | undefined): string => {
  if (!statusData) {
    return 'Pending';
  }

  if (
    statusData.lender_response === AGGREGATOR_STATUS.LENDER_RESPONSE.APPROVED ||
    statusData.lender_response === AGGREGATOR_STATUS.LENDER_RESPONSE.PRE_APPROVED
  ) {
    return 'Approved';
  }

  if (statusData.lender_response === AGGREGATOR_STATUS.LENDER_RESPONSE.DECLINED) {
    return 'Declined';
  }

  return 'Pending';
};

/**
 * Gets the appropriate status message from aggregator status data
 * Returns null if no message is available
 */
export const getAggregatorStatusMessage = (
  statusData: AggregatorStatusResponse | null | undefined,
): string | null => {
  if (!statusData) {
    return null;
  }

  if (
    (statusData.lender_response === AGGREGATOR_STATUS.LENDER_RESPONSE.APPROVED ||
      statusData.lender_response === AGGREGATOR_STATUS.LENDER_RESPONSE.PRE_APPROVED) &&
    statusData.lender_approved_message
  ) {
    return statusData.lender_approved_message;
  }

  if (
    statusData.lender_response === AGGREGATOR_STATUS.LENDER_RESPONSE.DECLINED &&
    statusData.lender_declined_message
  ) {
    return statusData.lender_declined_message;
  }

  if (
    statusData.lender_response === AGGREGATOR_STATUS.LENDER_RESPONSE.PENDING &&
    statusData.lender_pending_message
  ) {
    return statusData.lender_pending_message;
  }

  return null;
};

/**
 * Determines if offers should be displayed based on aggregator status
 */
export const shouldShowAggregatorOffers = (statusData: AggregatorStatusResponse | null | undefined): boolean => {
  if (!statusData) {
    return false;
  }

  const hasOffers = statusData.lender_offers && statusData.lender_offers.length > 0;

  return (
    hasOffers &&
    (statusData.lender_response === AGGREGATOR_STATUS.LENDER_RESPONSE.APPROVED ||
      statusData.lender_response === AGGREGATOR_STATUS.LENDER_RESPONSE.PRE_APPROVED ||
      statusData.submission_state === AGGREGATOR_STATUS.SUBMISSION_STATE.LENDER_RESPONDED)
  );
};
